// Technical indicators computed from candles [{t,o,h,l,c,v}]. Each returns [{i, value}] aligned to candle index.

export function sma(candles, n, key = 'c') {
  const out = []; let sum = 0;
  for (let i = 0; i < candles.length; i++) {
    sum += candles[i][key];
    if (i >= n) sum -= candles[i - n][key];
    if (i >= n - 1) out.push({ i, value: sum / n });
  }
  return out;
}

export function ema(candles, n, key = 'c') {
  const out = []; const k = 2 / (n + 1); let prev = null; let seed = 0;
  for (let i = 0; i < candles.length; i++) {
    const v = candles[i][key];
    if (i < n) { seed += v; if (i === n - 1) { prev = seed / n; out.push({ i, value: prev }); } continue; }
    prev = v * k + prev * (1 - k);
    out.push({ i, value: prev });
  }
  return out;
}

function emaOfValues(values, n) {
  const out = []; const k = 2 / (n + 1); let prev = null; let seed = 0; let count = 0;
  for (const { i, value } of values) {
    if (count < n) { seed += value; count++; if (count === n) { prev = seed / n; out.push({ i, value: prev }); } continue; }
    prev = value * k + prev * (1 - k);
    out.push({ i, value: prev });
  }
  return out;
}

export function bollinger(candles, n = 20, mult = 2) {
  const mid = [], upper = [], lower = [];
  for (let i = n - 1; i < candles.length; i++) {
    let s = 0; for (let j = i - n + 1; j <= i; j++) s += candles[j].c;
    const m = s / n;
    let v = 0; for (let j = i - n + 1; j <= i; j++) v += (candles[j].c - m) ** 2;
    const sd = Math.sqrt(v / n);
    mid.push({ i, value: m }); upper.push({ i, value: m + mult * sd }); lower.push({ i, value: m - mult * sd });
  }
  return { mid, upper, lower };
}

export function rsi(candles, n = 14) {
  const out = []; let gain = 0, loss = 0;
  for (let i = 1; i < candles.length; i++) {
    const d = candles[i].c - candles[i - 1].c;
    const g = Math.max(0, d), l = Math.max(0, -d);
    if (i <= n) { gain += g; loss += l; if (i === n) { gain /= n; loss /= n; out.push({ i, value: loss === 0 ? 100 : 100 - 100 / (1 + gain / loss) }); } continue; }
    gain = (gain * (n - 1) + g) / n; loss = (loss * (n - 1) + l) / n;
    out.push({ i, value: loss === 0 ? 100 : 100 - 100 / (1 + gain / loss) });
  }
  return out;
}

export function macd(candles, fast = 12, slow = 26, signal = 9) {
  const f = ema(candles, fast), s = ema(candles, slow);
  const fm = new Map(f.map((x) => [x.i, x.value]));
  const line = s.map(({ i, value }) => ({ i, value: fm.get(i) - value }));
  const sig = emaOfValues(line, signal);
  const sm = new Map(sig.map((x) => [x.i, x.value]));
  const hist = line.filter((x) => sm.has(x.i)).map(({ i, value }) => ({ i, value: value - sm.get(i) }));
  return { line, signal: sig, hist };
}

// Volume-weighted average price, reset each UTC day (intraday intervals only).
export function vwap(candles) {
  const out = []; let pv = 0, vol = 0, day = null;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const d = Math.floor(c.t / 86400e3);
    if (d !== day) { day = d; pv = 0; vol = 0; }
    const tp = (c.h + c.l + c.c) / 3;
    pv += tp * (c.v || 0); vol += c.v || 0;
    if (vol > 0) out.push({ i, value: pv / vol });
  }
  return out;
}
